import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ImageData, defaultFont, defaultFrame, BaseAttributeModel, SingleAttributeModel, MultiAttributeModel, TableAttributeModel, ImageAttributeModel, NondispleyAttributeModel } from './cardAttributeModelIndex';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

type TableRowValuesHashType = { [key in string]: string; }
export type TableRowType = {
  values: TableRowValuesHashType;
  order: number;
}

export type SingleAttribute = { cardId: string; cardTamplateAttributeId?: string; content: string; currentY: number; } & SingleAttributeModel;
export type MultiAttribute = { cardId: string; cardTamplateAttributeId?: string; content: string; currentY: number; currentHeight?: number; } & MultiAttributeModel;
export type TableAttribute = { cardId: string; cardTamplateAttributeId?: string; content: { [key in string]: TableRowType }; currentY: number; currentHeight?: number; } & TableAttributeModel;
export type ImageAttribute = { cardId: string; cardTamplateAttributeId?: string; content: ImageData; currentY: number; } & ImageAttributeModel;
export type NondispleyAttribute = { cardId: string; cardTamplateAttributeId?: string; content: string; } & NondispleyAttributeModel;

export type AttributeHavingFont = SingleAttribute | MultiAttribute | TableAttribute;

export type CardAttributeDb = SingleAttribute | MultiAttribute | TableAttribute | ImageAttribute | NondispleyAttribute;

const initialBase: BaseAttributeModel = {
  languageId: 'ja',
  name: '',
  hint: '',
  frame: defaultFrame,
  order: 0,
  x: 0,
  y: 0,
  z: 0,
  width: 100,
  height: 100,
  rotation: 0,
  description: {
    display: false,
    content: ''
  },
  background: {
    display: false,
    color: '#ffffff'
  },
  goodsId: undefined,
  version: undefined
};
type CardAttributeInitialType = CardAttributeDb[]
const initial: CardAttributeInitialType = [
  {
    ...initialBase,
    cardAttributeModel: 'single',
    font: defaultFont,
    cardId: '',
    cardTamplateAttributeId: undefined,
    defaultContent: '',
    content: '',
    currentY: 0,
  },
  {
    ...initialBase,
    cardAttributeModel: 'multi',
    font: defaultFont,
    cardId: '',
    cardTamplateAttributeId: undefined,
    defaultContent: '',
    isMarkdown: false,
    content: '',
    currentY: 0,
    currentHeight: undefined
  },
  {
    ...initialBase,
    cardAttributeModel: 'table',
    font: defaultFont,
    columnNames: {},
    cardId: '',
    cardTamplateAttributeId: undefined,
    content: {},
    currentY: 0,
    currentHeight: undefined
  },
  {
    ...initialBase,
    cardAttributeModel: 'image',
    defaultImage: {
      imageId: '',
      originWidth: 0,
      originHeight: 0,
      trimXFrom: 0,
      trimYFrom: 0,
      trimXTo: 0,
      trimYTo: 0
    },
    cardId: '',
    cardTamplateAttributeId: undefined,
    content: {
      imageId: '',
      originWidth: 0,
      originHeight: 0,
      trimXFrom: 0,
      trimYFrom: 0,
      trimXTo: 0,
      trimYTo: 0
    },
    currentY: 0
  },
  {
    ...initialBase,
    cardAttributeModel: 'nondisplay',
    cardId: '',
    cardTamplateAttributeId: undefined,
    defaultContent: '',
    content: '',
  }
]

export type CardAttributeDbHashType = {
  [key in string]: CardAttributeDb;
}

export type CardAttributeAddPayload = {
  id?: string;
} & CardAttributeDb;

export type CardAttributeUpdatePayload = {
  id: string;
} & (
  {[P in keyof SingleAttribute]?: SingleAttribute[P]} |
  {[P in keyof MultiAttribute]?: MultiAttribute[P]} |
  {[P in keyof TableAttribute]?: TableAttribute[P]} |
  {[P in keyof ImageAttribute]?: ImageAttribute[P]} |
  {[P in keyof NondispleyAttribute]?: NondispleyAttribute[P]}
)

export type CardAttributeDbState = {
  cardAttributes: CardAttributeDbHashType
}

export const generateCardAttributeId = (): string => generateId('ca');

const isCardAttributeDb = (obj: any): obj is CardAttributeDb => {
  return 'cardAttributeModel' in obj && 'cardId' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return isCardAttributeDb(a) && a.cardAttributeModel === b?.cardAttributeModel;
}
export const isSingleAttribute = (payload: any): payload is SingleAttribute => {
  return payload?.cardAttributeModel === 'single';
}
export const isMultiAttribute = (payload: any): payload is MultiAttribute => {
  return payload?.cardAttributeModel === 'multi';
}
export const isTableAttribute = (payload: any): payload is TableAttribute => {
  return payload?.cardAttributeModel === 'table';
}
export const isImageAttribute = (payload: any): payload is ImageAttribute => {
  return payload?.cardAttributeModel === 'image';
}
export const isNondisplayAttribute = (obj: any): obj is NondispleyAttribute => {
  return typeof obj === 'object' && !Array.isArray(obj) && 'cardAttributeModel' in obj && obj.cardAttributeModel === 'nondisplay';
}
export const isAttributeHavingFont = (payload: any): payload is AttributeHavingFont => {
  return payload?.cardAttributeModel === 'single' || payload?.cardAttributeModel === 'multi' || payload?.cardAttributeModel === 'table';
}

let initialState: CardAttributeDbState = { cardAttributes: {} };
const key = 'cardAttributes';

const cardAttributeDbSlice = createSlice({
  name: 'cardAttributeDb',
  initialState,
  reducers: {
    initializeCardAttributes(state: CardAttributeDbState) {
      state.cardAttributes = {};
    },
    addCardAttribute(state: CardAttributeDbState, action: PayloadAction<CardAttributeAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardAttributeId();
      state.cardAttributes[id] = obj;
      saveData(key, id, obj);
    },
    updateCardAttribute(state: CardAttributeDbState, action: PayloadAction<CardAttributeUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardAttributes[id] === undefined) {
        console.log(`*** CardAttribute not found: ${id}`);
      } else if (isSameType(state.cardAttributes[id], obj)) {
        const newData = { ...state.cardAttributes[id], ...obj };
        state.cardAttributes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardAttribute(state: CardAttributeDbState, action: PayloadAction<CardAttributeUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardAttributes[id];
      deleteData(key, id);
    },
    saveCardAttributes(state: CardAttributeDbState, action: PayloadAction<void>) {
      saveAllData(key, state.cardAttributes);
    },
    signUpCardAttributes(state: CardAttributeDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardAttributes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardAttributeDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardAttributes = updatedValues;
      if (changed) saveAllData(key, state.cardAttributes);
    })
  }
})

export const { initializeCardAttributes, addCardAttribute, updateCardAttribute, deleteCardAttribute, saveCardAttributes, signUpCardAttributes } = cardAttributeDbSlice.actions;
export default cardAttributeDbSlice.reducer;
